import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { X } from "@phosphor-icons/react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "object",
  "embed",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const LABELABLE_TAGS = new Set(["input", "meter", "output", "progress", "select", "textarea"]);

function normalizeReactId(id) {
  return String(id).replace(/[^a-zA-Z0-9_-]/g, "");
}

function findLabelableControl(node) {
  let match = null;

  Children.forEach(node, (child) => {
    if (match || !isValidElement(child)) return;

    const isNativeControl = typeof child.type === "string" && LABELABLE_TAGS.has(child.type);
    const isVisibleInput = child.type !== "input" || child.props.type !== "hidden";
    if (isNativeControl && isVisibleInput) {
      match = child;
      return;
    }

    if (child.props.children) match = findLabelableControl(child.props.children);
  });

  return match;
}

function bindFirstLabelableControl(node, controlId, bound = { value: false }) {
  return Children.map(node, (child) => {
    if (!isValidElement(child) || bound.value) return child;

    const isNativeControl = typeof child.type === "string" && LABELABLE_TAGS.has(child.type);
    const isVisibleInput = child.type !== "input" || child.props.type !== "hidden";
    if (isNativeControl && isVisibleInput) {
      bound.value = true;
      return cloneElement(child, { id: controlId });
    }

    if (!child.props.children) return child;
    const nestedChildren = bindFirstLabelableControl(child.props.children, controlId, bound);
    return bound.value ? cloneElement(child, undefined, nestedChildren) : child;
  });
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-ink">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-meta">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Page({ children }) {
  return <div className="mx-auto max-w-[1400px] animate-fade-up px-4 py-6 sm:p-8">{children}</div>;
}

const STATUS_TONE_MAP = {
  // Healthy or complete states.
  Active: "ok",
  Completed: "ok",
  Normal: "ok",
  Released: "ok",
  "QA Released": "ok",
  "In Stock": "ok",
  // States needing attention or human action.
  "In Analysis": "warn",
  "In Progress": "warn",
  "In Testing": "warn",
  "In Review": "warn",
  Submitted: "warn",
  "Revision Required": "warn",
  "Low Stock": "warn",
  Pending: "warn",
  "Pending Review": "warn",
  "Supervisor Review": "warn",
  "QA Review": "warn",
  // Failure, breach, or hold states.
  Abnormal: "critical",
  Critical: "critical",
  Expired: "critical",
  "On Hold": "critical",
  Rejected: "critical",
  // Informational workflow states use a non-brand status hue.
  Ordered: "info",
  Accepted: "info",
  Received: "info",
  Registered: "info",
  Reviewed: "info",
  "In Storage": "info",
  // Terminal or inactive states carry no urgency.
  Disposed: "neutral",
  Depleted: "neutral",
  Cancelled: "neutral",
  Draft: "neutral",
};

const STATUS_TONE_CLASS = {
  ok: "border-status-ok/30 bg-status-ok/10 text-status-ok",
  warn: "border-status-warn/30 bg-status-warn/10 text-status-warn",
  critical: "border-status-critical/30 bg-status-critical/10 text-status-critical",
  info: "border-status-info/30 bg-status-info/10 text-status-info",
  neutral: "border-status-neutral/30 bg-status-neutral/10 text-status-neutral",
};

/**
 * The tone a status carries, so anything that colors by status agrees with the
 * badge. The chart used to run its own regex over the status name and painted
 * Received and In Review the same orange, while their badges two inches away
 * were different colors.
 */
export function statusTone(status) {
  return STATUS_TONE_MAP[status || "Unknown"] || "neutral";
}

/** The status palette as a paintable color, for SVG and inline styles. */
export function statusColor(status) {
  return `rgb(var(--status-${statusTone(status)}))`;
}

/**
 * A status is a label, not prose.
 *
 * Without `whitespace-nowrap` a two-word status wrapped inside the pill
 * wherever the column was narrow, so the same control rendered 26px tall in
 * one row and 42px in the next, reading as an oval rather than a badge, and
 * dragging the row height with it. The badge keeps its shape; the column gives
 * it the room.
 */
export function StatusBadge({ status }) {
  const displayStatus = status || "Unknown";
  const tone = statusTone(displayStatus);

  return (
    <span
      aria-label={`Status: ${displayStatus}`}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS_TONE_CLASS[tone]}`}
    >
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {displayStatus}
    </span>
  );
}

export function Modal({ open, onClose, title, children, testid }) {
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const titleId = `dialog-title-${normalizeReactId(useId())}`;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;

    const dialog = dialogRef.current;
    previouslyFocusedRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const firstDataEntryControl = dialog?.querySelector(
      "[data-modal-initial-focus], input:not([disabled]):not([type='hidden']), select:not([disabled]), textarea:not([disabled])"
    );
    const firstFocusable = firstDataEntryControl || dialog?.querySelector(FOCUSABLE_SELECTOR);
    (firstFocusable || dialog)?.focus({ preventScroll: true });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }

      if (event.key !== "Tab" || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => !element.hidden && element.getAttribute("aria-hidden") !== "true"
      );

      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const focusIsOutside = !dialog.contains(document.activeElement);

      if (event.shiftKey && (document.activeElement === first || focusIsOutside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || focusIsOutside)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      const previouslyFocused = previouslyFocusedRef.current;
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-ink/45 p-4 backdrop-blur-sm"
      data-modal-overlay="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCloseRef.current?.();
      }}
    >
      <div
        aria-labelledby={titleId}
        aria-modal="true"
        className="relative flex max-h-[calc(100vh-2rem)] max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-line bg-white shadow-xl animate-fade-up"
        data-testid={testid}
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-line bg-white px-6 py-4">
          <h2 className="font-display text-lg font-semibold text-ink" id={titleId}>{title}</h2>
          <button
            aria-label="Close dialog"
            className="rounded-md p-1 text-meta transition-colors hover:bg-brand-soft hover:text-brand-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
            data-testid="modal-close"
            onClick={() => onCloseRef.current?.()}
            type="button"
          >
            <X aria-hidden="true" size={20} />
          </button>
        </div>
        <div className="min-h-0 overflow-y-auto p-6">{children}</div>
      </div>
    </div>,
    document.body
  );
}

export function Field({ label, error, errorTestId, children, id }) {
  const generatedId = `field-${normalizeReactId(useId())}`;
  const labelableControl = findLabelableControl(children);
  const controlId = id || labelableControl?.props.id || generatedId;
  const errorNode = error ? (
        <p data-testid={errorTestId} role="alert" className="mt-1.5 text-xs text-status-critical">
      {error}
    </p>
  ) : null;

  if (!labelableControl) {
    const groupLabelId = `${controlId}-label`;
    return (
      <div aria-labelledby={groupLabelId} role="group">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-meta" id={groupLabelId}>
          {label}
        </span>
        <div className="mt-1.5">{children}</div>
        {errorNode}
      </div>
    );
  }

  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-[0.15em] text-meta" htmlFor={controlId}>
        {label}
      </label>
      <div className="mt-1.5">{bindFirstLabelableControl(children, controlId)}</div>
      {errorNode}
    </div>
  );
}

export const inputErrorCls = "border-status-critical focus:ring-status-critical";

export const inputCls =
  "w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-ink transition-shadow placeholder:text-meta focus:border-transparent focus:outline-none focus:ring-2 focus:ring-brand disabled:cursor-not-allowed disabled:bg-surface disabled:opacity-70";

const buttonBaseCls =
  "inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60";

export function PrimaryButton({ children, className = "", ...props }) {
  return (
    <button
      className={`${buttonBaseCls} bg-brand text-white hover:bg-brand-hover ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, className = "", ...props }) {
  return (
    <button
      className={`${buttonBaseCls} border border-line bg-white text-ink hover:border-teal-100 hover:bg-brand-soft ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function DangerButton({ children, className = "", ...props }) {
  return (
    <button
      className={`${buttonBaseCls} bg-status-critical text-white hover:bg-status-critical/90 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      {Icon && (
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-lg bg-surface">
          <Icon className="text-meta" size={28} weight="duotone" />
        </div>
      )}
      <p className="font-medium text-ink">{title}</p>
      {subtitle && <p className="mt-1 text-sm text-meta">{subtitle}</p>}
    </div>
  );
}
